/**
 * Shared formatting and semantic coloring for Portfolio Cost & Savings.
 * Minimal color: only four financially evaluative metrics use favorable/unfavorable colors.
 */

/** Semantic palette: favorable = beneficial, unfavorable = costly. No bright red/green. */
export const FINANCIAL_COLORS = {
  favorable: '#0f9f6e',   // green/teal
  unfavorable: '#c75b39',  // muted red/rust
  neutral: '#1f2937',
  muted: '#6b7280',
} as const;

export type FinancialColorType = 'npv' | 'cumulative' | 'cost_per_ton' | 'cost_per_capita';

/**
 * Returns text color for the four financially evaluative columns only.
 * NPV: > 0 favorable, < 0 unfavorable.
 * Cumulative net cost / cost per ton / cost per capita: < 0 favorable, > 0 unfavorable.
 * Returns undefined for neutral (use default text color).
 */
export function getFinancialColor(
  value: number | null | undefined,
  type: FinancialColorType
): string | undefined {
  if (value == null || !Number.isFinite(value)) return undefined;
  if (type === 'npv') {
    if (value > 0) return FINANCIAL_COLORS.favorable;
    if (value < 0) return FINANCIAL_COLORS.unfavorable;
    return undefined;
  }
  // cumulative, cost_per_ton, cost_per_capita: negative = favorable, positive = unfavorable
  if (value < 0) return FINANCIAL_COLORS.favorable;
  if (value > 0) return FINANCIAL_COLORS.unfavorable;
  return undefined;
}

/** For charts: bar color by favorable (negative) vs unfavorable (positive) cost-per-ton style. */
export function getFinancialBarColor(value: number): string {
  if (value < 0) return FINANCIAL_COLORS.favorable;
  if (value > 0) return FINANCIAL_COLORS.unfavorable;
  return FINANCIAL_COLORS.muted;
}

export const currencyShort = (v: number): string => {
  const abs = Math.abs(v);
  if (abs >= 1e9) return (v < 0 ? '-$' : '$') + (abs / 1e9).toFixed(2) + 'B';
  if (abs >= 1e6) return (v < 0 ? '-$' : '$') + (abs / 1e6).toFixed(2) + 'M';
  return (v < 0 ? '-$' : '$') + Math.abs(Math.round(v)).toLocaleString();
};

/** Legacy: single-value semantic color (treats as cumulative/cost-per-ton logic). Use getFinancialColor for explicit type. */
export const costColor = (value: number): string | undefined => {
  if (value < 0) return FINANCIAL_COLORS.favorable;
  if (value > 0) return FINANCIAL_COLORS.unfavorable;
  return undefined;
};

export const TOOLTIP_NEGATIVE_SAVINGS = 'Negative = net savings (benefit).';
export const TOOLTIP_POSITIVE_COST = 'Positive = net cost.';

/** Short note for tables: green/red meaning. */
export const FINANCIAL_COLOR_NOTE = 'Green indicates financially favorable outcomes (net savings); red indicates net cost.';
